/**
 * @file Custom print configurator redirect.
 *
 * Responsible for redirecting the user to their first custom print with
 * an order status of CUSTOMER_INPUT.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { withRouter, Redirect } from 'react-router-dom';
import get from 'lodash.get';

import { MeWithPurchasedAlbumPrintsByAlbum } from '../../../graphql/queries/account';
import { ORDER_STATUS } from '../../../constants/pro-custom-prints';
import Spinner from '../../../components/global/Spinner';
import ContentWrap from '../../../components/global/ContentWrap';
import { getAsset } from '../../../util/assets';

class AlbumCustomPrintConfigurationRedirect extends Component {
  static propTypes = {
    loading: PropTypes.bool,
    pendingPurchasedCustomPrints: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
    ),
    match: PropTypes.shape({
      params: PropTypes.shape({
        albumId: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  };

  render() {
    const { loading, pendingPurchasedCustomPrints, match } = this.props;
    const { albumId } = match.params;
    const [firstPendingCustomPrint] = pendingPurchasedCustomPrints || [];

    if (loading || !pendingPurchasedCustomPrints) {
      return (
        <div className="text-center py-5">
          <Spinner />
        </div>
      );
    }

    if (firstPendingCustomPrint) {
      return (
        <Redirect
          to={`/purchase/album/${albumId}/custom-prints/${firstPendingCustomPrint.id}`}
        />
      );
    }

    return (
      <div className="wrapper-main">
        <div className="content-wrap content-wrap-gray">
          <ContentWrap size="sm">
            <div className="text-center">
              <h2 className="mb-3">No prints need your attention yet!</h2>
              <img
                width="100px"
                src={getAsset('/waldo-icon.svg')}
                alt="Confused Waldo Icon"
                className="mb-3"
              />

              <p className="text-muted m-0">
                Either we&rsquo;ve got &rsquo;em all &hellip;configured out or
                you should be expecting some more photos. If so, I&rsquo;ll let
                you know soon!
              </p>
            </div>
          </ContentWrap>
        </div>
      </div>
    );
  }
}

const withCustomerInputPurchasedCustomPrints = graphql(
  MeWithPurchasedAlbumPrintsByAlbum,
  {
    options: ({ match }) => ({
      variables: {
        albumId: match.params.albumId,
        orderStatuses: [ORDER_STATUS.CUSTOMER_INPUT],
      },
      fetchPolicy: 'cache-and-network',
    }),
    props: ({ data: { me, loading } }) => ({
      loading,
      pendingPurchasedCustomPrints: get(
        me,
        'purchased_custom_prints.records',
        [],
      ),
    }),
    skip: ({ match }) => !match.params.albumId,
  },
);

export default compose(
  withRouter,
  withCustomerInputPurchasedCustomPrints,
)(AlbumCustomPrintConfigurationRedirect);
